import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Button } from "../../components/Button";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { SelectInput } from "../../components/SelectInput";
import {
  MAX_SALES_PERIOD_DAYS,
  getSalesToday,
  type SalesPeriodPreset,
} from "../../utils/dailySalesUtils";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const PRESET_OPTIONS: { key: SalesPeriodPreset }[] = [
  { key: "thisMonth" },
  { key: "lastMonth" },
  { key: "last30Days" },
  { key: "custom" },
];

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const PeriodSelect = styled(SelectInput)`
  width: auto;
  min-width: 120px;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.x1}px;
  color: ${colors.text};
  background: transparent;
  border: none;
  border-radius: ${borderRadius.regular}px;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    color: ${colors.primary};
    background: ${colors.backgroundDark};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PeriodLabel = styled.button`
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  background-color: ${colors.background};
  border: none;
  border-radius: ${borderRadius.regular}px;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;

  &:hover {
    background: ${colors.backgroundLight};
  }
`;

const ModalHint = styled.p`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const dateInputStyle = {
  padding: `${spacing.x1}px ${spacing.x2}px`,
  border: `1px solid ${colors.border}`,
  borderRadius: 4,
  fontSize: fontSizes.regular,
};

interface SalesPeriodSelectorProps {
  period: { from: string; to: string };
  periodPreset: SalesPeriodPreset;
  periodLabel: string;
  canGoForward: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onPeriodChange: (from: string, to: string) => void;
  onPresetChange: (preset: SalesPeriodPreset) => void;
}

export const SalesPeriodSelector = ({
  period,
  periodPreset,
  periodLabel,
  canGoForward,
  onPrevious,
  onNext,
  onPeriodChange,
  onPresetChange,
}: SalesPeriodSelectorProps) => {
  const { t } = useTranslation();
  const today = getSalesToday().toFormat("yyyy-MM-dd");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pickerStart, setPickerStart] = useState(period.from);
  const [pickerEnd, setPickerEnd] = useState(period.to);

  const handleOpenModal = () => {
    setPickerStart(period.from);
    setPickerEnd(period.to);
    setIsModalOpen(true);
  };

  const handleApply = () => {
    onPeriodChange(pickerStart, pickerEnd);
    setIsModalOpen(false);
  };

  const handlePresetSelect = (preset: SalesPeriodPreset) => {
    if (preset === "custom") {
      onPresetChange("custom");
      handleOpenModal();
      return;
    }
    onPresetChange(preset);
  };

  return (
    <>
      <Controls>
        <PeriodSelect
          value={periodPreset}
          onChange={(e) =>
            handlePresetSelect(e.target.value as SalesPeriodPreset)
          }
        >
          {PRESET_OPTIONS.map(({ key }) => (
            <option key={key} value={key}>
              {t(`adminDailySales.period.${key}`)}
            </option>
          ))}
        </PeriodSelect>
        <NavRow>
          <NavButton
            onClick={onPrevious}
            aria-label={t("statistics.previousPeriod")}
          >
            <ChevronLeft size={20} />
          </NavButton>
          <PeriodLabel onClick={handleOpenModal}>{periodLabel}</PeriodLabel>
          <NavButton
            onClick={onNext}
            disabled={!canGoForward}
            aria-label={t("statistics.nextPeriod")}
          >
            <ChevronRight size={20} />
          </NavButton>
        </NavRow>
      </Controls>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t("statistics.selectPeriod")}
        maxWidth={400}
      >
        <ModalHint>
          {t("adminDailySales.maxPeriodDays", { days: MAX_SALES_PERIOD_DAYS })}
        </ModalHint>
        <FormGroup style={{ marginBottom: spacing.x2 }}>
          <FormLabel>{t("statistics.startDate")}</FormLabel>
          <input
            type="date"
            value={pickerStart}
            onChange={(e) => setPickerStart(e.target.value)}
            max={today}
            style={dateInputStyle}
          />
        </FormGroup>
        <FormGroup style={{ marginBottom: spacing.x2 }}>
          <FormLabel>{t("statistics.endDate")}</FormLabel>
          <input
            type="date"
            value={pickerEnd}
            onChange={(e) => setPickerEnd(e.target.value)}
            max={today}
            style={dateInputStyle}
          />
        </FormGroup>
        <Button onClick={handleApply}>{t("statistics.apply")}</Button>
      </Modal>
    </>
  );
};
